// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, useState, type Ref } from 'react';

import type { GeoSource, TreeItem, TreeStorey } from '../../../bygning/geometri';
import styles from './ElementTree.module.css';

export interface ElementTreeProps {
  tree: readonly TreeStorey[];
  /** The selected element's guid (`?el=`). */
  selected: string | null;
  onSelect: (guid: string) => void;
  /** The storey the plan and the clipping box show (the StoreyPicker's), or null for all. */
  storey: number | null;
  /** Picks a storey for the plan and the clipping box (the same state the StoreyPicker sets); null = all. */
  onStorey: (id: number | null) => void;
  /** Every selected guid (the plan's multi-selection), drawn selected too; defaults to `selected` alone. */
  selectedAll?: readonly string[];
  /**
   * The CAD browser's slim form (frontend-u0o.9): the storeys other than the
   * one shown start folded, and every group starts folded to its count.
   */
  compact?: boolean;
}

const SOURCE_TITLE: Record<GeoSource, string> = {
  detected: 'Detekteret fra scanningen',
  manual: 'Oprettet eller rettet i hånden',
  imported: 'Importeret fra en BIM-model',
  derived: 'Afledt af rum og etager',
};

/** A group of more rows than this starts folded. */
const FOLD_ABOVE = 40;

const groupKey = (storey: number | null, kind: string) => `${storey ?? 'none'}:${kind}`;

/**
 * Rediger › Geometri's element tree (frontend-u0o.2): storeys, then Vægge,
 * Åbninger, Rum, Dæk & tag, then the rows with their badges — source dot,
 * ✎ edited, lock, ? uncertain, ⚠ warnings. One selection with the plan and
 * the 3D model; the selected row is unfolded to and scrolled into view. A
 * storey's name picks it for the plan and the clipping box (the
 * StoreyPicker's state), its chevron folds it.
 */
export function ElementTree({ tree, selected, onSelect, storey, onStorey, selectedAll, compact = false }: ElementTreeProps) {
  const [folded, setFolded] = useState<Record<string, boolean>>({});
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  const isFolded = (key: string, size: number) => folded[key] ?? (compact || size > FOLD_ABOVE);
  const picked = new Set(selectedAll ?? (selected === null ? [] : [selected]));
  const toggle = (key: string, size: number) => setFolded((f) => ({ ...f, [key]: !isFolded(key, size) }));

  // Unfold to a selection made elsewhere (the plan, 3D, a warning).
  useEffect(() => {
    if (selected === null) return;
    for (const s of tree) {
      for (const g of s.groups) {
        if (!g.items.some((i) => i.guid === selected)) continue;
        const sk = `storey:${s.id ?? 'none'}`;
        const gk = groupKey(s.id, g.kind);
        setFolded((f) => (f[sk] === false && f[gk] === false ? f : { ...f, [sk]: false, [gk]: false }));
      }
    }
  }, [selected, tree]);
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selected, folded]);

  if (tree.length === 0) return null;
  return (
    <ul className={styles.tree} aria-label="Elementer">
      {tree.map((s) => {
        const sk = `storey:${s.id ?? 'none'}`;
        const sFolded = folded[sk] ?? (compact && storey !== null && s.id !== storey);
        const current = s.id !== null && s.id === storey;
        return (
          <li key={sk}>
            <div className={styles.storeyRow}>
              <button
                type="button"
                className={styles.chevron}
                aria-expanded={!sFolded}
                aria-label={sFolded ? `Fold ${s.name} ud` : `Fold ${s.name} sammen`}
                onClick={() => setFolded((f) => ({ ...f, [sk]: !sFolded }))}
              >
                <Chevron open={!sFolded} />
              </button>
              <button
                type="button"
                className={current ? `${styles.storeyName} ${styles.storeyOn}` : styles.storeyName}
                aria-pressed={current}
                disabled={s.id === null}
                title={s.id === null ? undefined : current ? 'Vis alle etager' : `Vis kun ${s.name} på planen og i klippeboksen`}
                onClick={() => onStorey(current ? null : s.id)}
              >
                {s.name}
              </button>
              <span className={styles.count}>{s.count}</span>
            </div>
            {!sFolded && (
              <ul className={styles.groups}>
                {s.groups.length === 0 && <li className={styles.empty}>Ingen elementer</li>}
                {s.groups.map((g) => {
                  const gk = groupKey(s.id, g.kind);
                  const gFolded = isFolded(gk, g.items.length);
                  return (
                    <li key={gk}>
                      <button type="button" className={styles.groupRow} aria-expanded={!gFolded} onClick={() => toggle(gk, g.items.length)}>
                        <Chevron open={!gFolded} />
                        <span className={styles.groupName}>{g.label}</span>
                        <span className={styles.count}>{g.items.length}</span>
                      </button>
                      {!gFolded && (
                        <ul className={styles.items}>
                          {g.items.map((item) => (
                            <li key={item.guid}>
                              <ItemRow
                                item={item}
                                on={picked.has(item.guid)}
                                buttonRef={item.guid === selected ? selectedRef : undefined}
                                onSelect={() => onSelect(item.guid)}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ItemRow({
  item,
  on,
  onSelect,
  buttonRef,
}: {
  item: TreeItem;
  on: boolean;
  onSelect: () => void;
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-pressed={on}
      className={on ? `${styles.item} ${styles.itemOn}` : styles.item}
      onClick={onSelect}
    >
      <span className={`${styles.dot} ${styles[`dot_${item.source}`] ?? ''}`} title={SOURCE_TITLE[item.source]} aria-hidden="true" />
      <span className={styles.itemLabel}>{item.label}</span>
      <span className={styles.badges}>
        {item.edited && (
          <span className={styles.badge} title="Rettet i hånden">
            ✎<span className={styles.sr}> rettet</span>
          </span>
        )}
        {item.locked && (
          <span className={styles.badge} title="Låst: en ny detektering overskriver den ikke">
            <LockIcon />
            <span className={styles.sr}> låst</span>
          </span>
        )}
        {item.uncertain && (
          <span className={`${styles.badge} ${styles.uncertain}`} title="Usikker: lav sikkerhed eller antaget tykkelse">
            ?<span className={styles.sr}> usikker</span>
          </span>
        )}
        {item.warnings > 0 && (
          <span className={`${styles.badge} ${styles.warn}`} title={`${item.warnings} ${item.warnings === 1 ? 'advarsel' : 'advarsler'}`}>
            ⚠{item.warnings > 1 ? item.warnings : ''}
            <span className={styles.sr}> advarsler</span>
          </span>
        )}
      </span>
    </button>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg className={open ? `${styles.chev} ${styles.chevOpen}` : styles.chev} viewBox="0 0 12 12" aria-hidden="true">
      <path d="M4 2.5 7.5 6 4 9.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className={styles.lock} viewBox="0 0 12 12" aria-hidden="true">
      <rect x="2.5" y="5.5" width="7" height="5" rx="1" fill="currentColor" />
      <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
